import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { ArrowRight, ShieldCheck, Zap, Eye, Check } from "lucide-react";
import { TosModal, PrivacyModal } from "../components/LegalModals";
import ContactModal from "../components/ContactModal";
import api from "../lib/api";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/72787016-fe41-45ba-b43d-2a00bb731c23/images/2baeb5967d0762f020b360670678cd4b4edc919ba18f2311476dc7f748bb6984.png";

export default function Landing() {
  const nav = useNavigate();
  const [tos, setTos] = useState(false);
  const [pri, setPri] = useState(false);
  const [contact, setContact] = useState(false);
  const [region, setRegion] = useState(null);

  useEffect(() => {
    api.get("/region").then(({ data }) => setRegion(data)).catch(() => {});
  }, []);

  const symbol = region?.symbol || "$";
  const curLabel = (region?.currency || "usd").toUpperCase();

  return (
    <div className="min-h-screen bg-obsidian text-white overflow-x-hidden">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-obsidian/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-3">
            <Button
              data-testid="landing-signin-btn"
              variant="ghost"
              className="text-white/70 hover:text-white"
              onClick={() => nav("/login")}
            >
              Sign in
            </Button>
            <Button
              data-testid="landing-cta-top"
              onClick={() => nav("/login")}
              className="bg-teal hover:bg-teal-hover text-white rounded-sm"
            >
              Start free trial <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div
          className="absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(180deg, black 30%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(180deg, black 30%, transparent 100%)",
          }}
        />
        <div className="absolute inset-0 -z-10 sr-stripe opacity-30" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="sr-pill text-teal mb-6" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-teal sr-blink" /> Enterprise · S.A.I.
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
              Draft <span className="text-teal">credible</span>, leadership-grade<br />
              communications in <em className="not-italic font-light">seconds</em>.
            </h1>
            <p className="mt-6 max-w-xl text-white/60 text-base leading-relaxed">
              SaiResponse is the smart agent interface trusted Support Directors, Account Executives, and Ops leaders use to defuse escalations, run incidents, and protect revenue — without losing their composure.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="landing-cta-primary"
                size="lg"
                onClick={() => nav("/login")}
                className="bg-teal hover:bg-teal-hover text-white rounded-sm px-6 h-12 text-base"
              >
                Continue with Google <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <span className="text-xs text-white/40 font-mono uppercase tracking-[0.16em]">
                3-day frictionless trial · No card required
              </span>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "Personas", v: "3" },
                { k: "Modes", v: "9" },
                { k: "Retention", v: "0 logs" },
              ].map((s) => (
                <div key={s.k} className="border-l-2 border-teal/40 pl-3">
                  <div className="text-2xl font-display font-black text-white">{s.v}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40 mt-1">{s.k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: sample card */}
          <div className="lg:col-span-5">
            <div className="sr-card sr-grain p-5 sr-fade-in" data-testid="hero-sample">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono text-white/40 mb-3">
                <span className="h-1.5 w-1.5 bg-teal rounded-full" />
                Persona — Enterprise Support Leadership · Mode: Customer Escalation
              </div>
              <div className="text-xs font-mono text-white/30 mb-2">// INPUT</div>
              <div className="text-sm text-white/70 leading-relaxed border-l-2 border-white/10 pl-3 mb-5">
                "This is the third outage in five weeks. My CFO is asking why we're paying $180k/year for a platform that keeps falling over…"
              </div>
              <div className="text-xs font-mono text-teal/80 mb-2">// SAI RESPONSE →</div>
              <div className="text-sm text-white/90 leading-relaxed">
                Mark, thank you for the directness — it is warranted. The repeated incidents are not the experience we committed to, and I take full ownership of the path forward. Here is what is true today, and what changes by Monday…
              </div>
              <div className="mt-5 sr-divider" />
              <div className="mt-3 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/40">
                <Check className="h-3 w-3 text-teal" /> 0.0s · No data stored
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature triad */}
      <section className="border-t border-border bg-charcoal/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-6">
          {[
            { i: ShieldCheck, t: "Zero Retention by Design", d: "Your pasted text never touches our database. Drafts are generated in-memory and discarded on response." },
            { i: Zap, t: "Cross-Functional Personas", d: "Three role archetypes × three modes each — escalations, incidents, briefings, SLA disputes, hiring feedback." },
            { i: Eye, t: "Tenant-Isolated", d: "Strict per-user partitioning. No admin can see what you write — by architecture, not by policy." },
          ].map((f, i) => (
            <div key={i} className="sr-card p-6">
              <f.i className="h-5 w-5 text-teal mb-4" />
              <h3 className="font-display text-lg text-white tracking-tight">{f.t}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-border" id="pricing">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="mb-10">
            <div className="sr-pill text-teal">Pricing</div>
            <h2 className="mt-4 font-display text-3xl text-white tracking-tight">Try free for 3 days. Upgrade when ready.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="sr-card p-7" data-testid="pricing-monthly">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/50">Monthly</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-display font-black">{symbol}29</span>
                <span className="text-white/50">/month · {curLabel}</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm text-white/70">
                {["Unlimited generations", "All 9 persona modes", "API key for Chrome extension", "Cancel anytime"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-teal" />{x}</li>
                ))}
              </ul>
            </div>
            <div className="sr-card p-7 sr-teal-glow relative" data-testid="pricing-yearly">
              <span className="absolute -top-2.5 right-5 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 bg-teal text-white rounded-sm">Save 30%</span>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-teal">Yearly</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-display font-black">{symbol}240</span>
                <span className="text-white/50">/year · {curLabel}</span>
              </div>
              <div className="text-sm text-teal/90 font-mono mt-1">≈ {symbol}20/mo billed annually</div>
              <ul className="mt-6 space-y-2 text-sm text-white/70">
                {["Everything in Monthly", "Best value", "Priority support", "Lock in pricing"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-teal" />{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <Logo size={28} showTagline={false} />
          <div className="flex items-center gap-5 text-sm text-white/50">
            <button data-testid="footer-contact" onClick={() => setContact(true)} className="hover:text-white">Contact / Help</button>
            <button data-testid="footer-tos" onClick={() => setTos(true)} className="hover:text-white">Terms of Service</button>
            <button data-testid="footer-privacy" onClick={() => setPri(true)} className="hover:text-white">Privacy Policy</button>
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/30">© 2026 SaiResponse · {curLabel}</span>
          </div>
        </div>
      </footer>

      <TosModal open={tos} onOpenChange={setTos} />
      <PrivacyModal open={pri} onOpenChange={setPri} />
      <ContactModal open={contact} onOpenChange={setContact} />
    </div>
  );
}
